/**
 * Consent Drawer Component
 *
 * Right-side drawer displaying consent and data logs with timeline visualization.
 * Transforms API responses into chronological timelines using Equal DS Timeline component.
 */

import React, { useState, useEffect } from 'react';
import {
  Drawer,
  DrawerHeader,
  DrawerContent,
  DrawerOverlay,
  TabSwitcher,
  Loader,
} from 'equal-ds-ui';
import { FileText, AlertCircle } from 'lucide-react';
import { ConsentTimelineView } from './ConsentTimelineView';
import { DataTimelineView } from './DataTimelineView';
import { UserActivityLogsView } from './UserActivityLogsView';
import { OrchestrationStatusView } from './OrchestrationStatusView';
import { calculateOverallTAT } from '../../../utils/timeline-transformers';
import type { ConsentDrawerProps, DrawerTab } from '../../../types/consent';
import type { ConsentTimeline, SessionTimeline, DataSession } from '../../../types/timeline';
import './finpro-logs-drawer-styles.css';

export const ConsentDrawer: React.FC<ConsentDrawerProps> = ({
  isOpen,
  selectedConsent,
  activeTab,
  onTabChange,
  onClose,
  consentTimeline,
  dataSessions,
  metadata: _metadata,
  error,
  isLoading,
  activityLogs,
  activityLogsError,
  activityLogsLoading,
  showActivityTab = false,
  orchestrationData,
  orchestrationError,
  orchestrationLoading,
  showOrchestrationTab = false,
}) => {

  // State for selected data session
  const [selectedSessionId, setSelectedSessionId] = useState<string>('');

  // Prepare consent timeline data (already transformed from V2 API)
  const consentTimelineData: ConsentTimeline | null = consentTimeline && consentTimeline.length > 0
    ? {
        events: consentTimeline,
        overallTAT: calculateOverallTAT(consentTimeline),
      }
    : null;

  // Prepare data sessions (already transformed from V2 API)
  const dataSessionsList = dataSessions || [];

  // Auto-select first session when consent changes or drawer opens
  useEffect(() => {
    if (dataSessionsList.length > 0) {
      // Always reset to first session when:
      // 1. Consent changes (different consent_id or consent_handle)
      // 2. Drawer opens for the first time
      // 3. Sessions data loads
      setSelectedSessionId(dataSessionsList[0].sessionId);
    } else {
      // Clear selection if no sessions available
      setSelectedSessionId('');
    }
  }, [
    selectedConsent?.consent_id,
    selectedConsent?.consent_handle,
    dataSessionsList.length,
  ]);

  // Get timeline for selected session
  const sessionTimeline: SessionTimeline | null = selectedSessionId && dataSessionsList.length > 0
    ? (() => {
        const session = dataSessionsList.find(s => s.sessionId === selectedSessionId);
        if (!session) return null;

        const sessionDataSession: DataSession = {
          sessionId: session.sessionId,
          status: session.status as 'Active' | 'Failed' | 'Data Ready' | 'Partial Data',
          statusVariant: session.statusVariant,
          accounts: session.accounts, // Already transformed to camelCase by transformer
        };

        return {
          session: sessionDataSession,
          events: session.timeline,
          overallTAT: calculateOverallTAT(session.timeline),
        };
      })()
    : null;

  // Early return if not open or no consent selected
  if (!isOpen || !selectedConsent) {
    return null;
  }

  // Show loading state while fetching timeline
  if (isLoading) {
    return (
      <>
        <DrawerOverlay />
        <Drawer
          variant="overlay"
          width="60%"
          className="border-border-default bg-gradient-to-b from-background-secondary to-primary-50"
        >
          <DrawerHeader
            title="View Logs"
            subtitle="View complete logs for consent and data journeys of your users"
            icon={<FileText className="w-5 h-5 bg-transparent" />}
            onClose={onClose}
          />
          <DrawerContent className="flex flex-col flex-1 items-center justify-center">
            <div className="flex flex-col items-center text-center px-6 py-12">
              <Loader size={80} cellSize={20} />
              <p className="text-sm font-medium text-gray-700 mt-6">Loading timeline data...</p>
              <p className="text-xs text-gray-500 mt-2">Please wait while we fetch the logs</p>
            </div>
          </DrawerContent>
        </Drawer>
      </>
    );
  }

  // Show error state if API failed
  if (error) {
    return (
      <>
        <DrawerOverlay />
        <Drawer
          variant="overlay"
          width="60%"
          className="border-border-default bg-gradient-to-b from-background-secondary to-primary-50"
        >
          <DrawerHeader
            title="View Logs"
            subtitle="View complete logs for consent and data journeys of your users"
            icon={<FileText className="w-5 h-5 bg-transparent" />}
            onClose={onClose}
          />
          <DrawerContent className="flex flex-col flex-1 items-center justify-center">
            <div className="flex flex-col items-center text-center max-w-md px-6 py-12">
              <AlertCircle className="w-16 h-16 text-red-500 mb-4" />
              <h3 className="text-lg font-semibold text-gray-900 mb-2">Error Loading Timeline</h3>
              <p className="text-sm text-gray-600 mb-6">{error}</p>
              <button
                onClick={onClose}
                className="px-6 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
              >
                Close
              </button>
            </div>
          </DrawerContent>
        </Drawer>
      </>
    );
  }

  return (
    <>
      <DrawerOverlay />
      <Drawer
        variant="overlay"
        width="60%"
        className="border-border-default bg-gradient-to-b from-background-secondary to-primary-50"
      >
        <DrawerHeader
          title="View Logs"
          subtitle="View complete logs for consent and data journeys of your users"
          icon={<FileText className="w-5 h-5 bg-transparent" />}
          onClose={onClose}
        />

        <DrawerContent className="flex flex-col flex-1 overflow-y-auto">
          {/* Tab Switcher — third tab is gated by showActivityTab (FP-6471) */}
          <TabSwitcher
            tabs={[
              { id: 'consent', label: 'Consent Logs' },
              { id: 'data', label: 'Data Logs' },
              ...(showActivityTab
                ? [{ id: 'activity', label: 'User Activity Logs' }]
                : []),
              ...(showOrchestrationTab
                ? [{ id: 'orchestration', label: 'Orchestration Logs' }]
                : []),
            ]}
            activeTab={activeTab}
            onTabChange={(tabId) => onTabChange(tabId as DrawerTab)}
            variant="horizontal"
            className="rounded-xl mb-2"
          />

          {/* Scrollable Content Area */}
          <div className="flex-1 min-h-0 finpro-logs-scrollable finpro-logs-content flex flex-col">
            {activeTab === 'consent' ? (
              <ConsentTimelineView timeline={consentTimelineData} />
            ) : activeTab === 'data' ? (
              <DataTimelineView
                sessions={dataSessionsList}
                sessionTimeline={sessionTimeline}
                selectedSessionId={selectedSessionId}
                onSessionSelect={setSelectedSessionId}
              />
            ) : activeTab === 'activity' && showActivityTab ? (
              <UserActivityLogsView
                logs={activityLogs ?? null}
                isLoading={!!activityLogsLoading}
                error={activityLogsError ?? null}
              />
            ) : activeTab === 'orchestration' && showOrchestrationTab ? (
              <OrchestrationStatusView
                data={orchestrationData ?? null}
                isLoading={!!orchestrationLoading}
                error={orchestrationError ?? null}
              />
            ) : (
              <ConsentTimelineView timeline={consentTimelineData} />
            )}
          </div>
        </DrawerContent>
      </Drawer>
    </>
  );
};
